/**
 * LanguageSwitcher Component - Multi-language selector for forms
 */

import React from 'react';
import { Globe } from 'lucide-react';
import { Language, getLanguageName, getLanguageFlag } from '../../hooks/forms/useMultiLanguage';

export interface LanguageSwitcherProps {
  currentLanguage: Language;
  availableLanguages: Language[];
  onLanguageChange: (language: Language) => void;
  variant?: 'buttons' | 'dropdown' | 'flags';
  className?: string;
  disabled?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  currentLanguage,
  availableLanguages,
  onLanguageChange,
  variant = 'buttons',
  className = '',
  disabled = false,
}) => {
  if (availableLanguages.length <= 1) {
    return null;
  }

  // Button variant
  if (variant === 'buttons') {
    return (
      <div className={`inline-flex rounded-md shadow-sm ${className}`} role="group">
        {availableLanguages.map((lang, index) => {
          const isActive = lang === currentLanguage;
          const isFirst = index === 0;
          const isLast = index === availableLanguages.length - 1;

          return (
            <button
              key={lang}
              type="button"
              onClick={() => onLanguageChange(lang)}
              disabled={disabled || isActive}
              className={`
                px-4 py-2 text-sm font-medium
                ${isFirst ? 'rounded-l-lg' : ''}
                ${isLast ? 'rounded-r-lg' : ''}
                ${!isFirst ? 'border-l-0' : ''}
                ${
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600 z-10'
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                focus:z-10 focus:ring-2 focus:ring-blue-500 focus:outline-none
                transition-colors duration-200
              `}
              aria-pressed={isActive}
              aria-label={`Switch to ${getLanguageName(lang)}`}
            >
              {lang.toUpperCase()}
            </button>
          );
        })}
      </div>
    );
  }

  // Flags variant
  if (variant === 'flags') {
    return (
      <div className={`inline-flex gap-2 ${className}`}>
        {availableLanguages.map((lang) => {
          const isActive = lang === currentLanguage;

          return (
            <button
              key={lang}
              type="button"
              onClick={() => onLanguageChange(lang)}
              disabled={disabled || isActive}
              className={`
                p-2 rounded-lg text-2xl
                ${
                  isActive
                    ? 'bg-blue-100 ring-2 ring-blue-500'
                    : 'hover:bg-gray-100'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                transition-all duration-200
              `}
              title={getLanguageName(lang)}
              aria-pressed={isActive}
              aria-label={`Switch to ${getLanguageName(lang)}`}
            >
              {getLanguageFlag(lang)}
            </button>
          );
        })}
      </div>
    );
  }

  // Dropdown variant
  return (
    <div className={`relative inline-block ${className}`}>
      <select
        value={currentLanguage}
        onChange={(e) => onLanguageChange(e.target.value as Language)}
        disabled={disabled}
        className={`
          pl-10 pr-4 py-2
          bg-white border border-gray-300 rounded-md shadow-sm
          text-sm font-medium text-gray-700
          hover:bg-gray-50
          focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          appearance-none
        `}
        aria-label="Select language"
      >
        {availableLanguages.map((lang) => (
          <option key={lang} value={lang}>
            {getLanguageName(lang)}
          </option>
        ))}
      </select>
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
        <Globe className="w-4 h-4 text-gray-500" />
      </div>
    </div>
  );
};

export default LanguageSwitcher;